import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Typography, Card, Button, Space, Select, message, Descriptions, Tag, Tree, Modal } from 'antd';
import { CheckCircleOutlined, PlayCircleOutlined, UserOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { testRunService } from '../services/testRunService';
import { userService } from '../services/userService';
import { TestRun, TestRunStatus, TestRunItem, TestRunItemStatus } from '../types/testRun';
import { User } from '../types/user';
import { TestItem } from '../types/testItem';
import { AddTestItemsModal } from './TestRuns/AddTestItemsModal';

const { Title } = Typography;
const { Option } = Select;

const TestRunPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [testRun, setTestRun] = useState<TestRun | null>(null);
    const [loading, setLoading] = useState(false);
    const [isAddModalVisible, setIsAddModalVisible] = useState(false);
    const [users, setUsers] = useState<User[]>([]);

    useEffect(() => {
        if (id) {
            fetchTestRun(parseInt(id));
            fetchUsers();
        }
    }, [id]);

    const fetchUsers = async () => {
        try {
            const data = await userService.getUsers();
            setUsers(data);
        } catch (error) {
            console.error('Error fetching users:', error);
            message.error(t('users.fetchError'));
        }
    };

    const fetchTestRun = async (testRunId: number) => {
        try {
            setLoading(true);
            const data = await testRunService.getTestRun(testRunId);
            setTestRun(data);
        } catch (error) {
            console.error('Error fetching test run:', error);
            message.error(t('testRuns.fetchError'));
        } finally {
            setLoading(false);
        }
    };

    const handleStart = async () => {
        if (!testRun) return;
        try {
            setLoading(true);
            await testRunService.startTestRun(testRun.id);
            await fetchTestRun(testRun.id);
            message.success(t('testRuns.startSuccess'));
        } catch (error) {
            message.error(t('testRuns.startError'));
        } finally {
            setLoading(false);
        }
    };

    const handleComplete = async () => {
        if (!testRun) return;
        try {
            setLoading(true);
            await testRunService.completeTestRun(testRun.id);
            await fetchTestRun(testRun.id);
            message.success(t('testRuns.completeSuccess'));
        } catch (error) {
            message.error(t('testRuns.completeError'));
        } finally {
            setLoading(false);
        }
    };

    const handleAssignUser = async (itemId: number, userId: number | null) => {
        try {
            await testRunService.assignUserToItem(itemId, userId || undefined);
            await fetchTestRun(testRun!.id);
            message.success(t('testRuns.assignSuccess'));
        } catch (error) {
            console.error('Error assigning user:', error);
            message.error(t('testRuns.assignError'));
        }
    };

    const handleStatusChange = async (itemId: number, status: TestRunItemStatus) => {
        try {
            await testRunService.updateTestItemStatus(itemId, status);
            await fetchTestRun(testRun!.id);
            message.success(t('testRuns.statusUpdateSuccess'));
        } catch (error) {
            message.error(t('testRuns.statusUpdateError'));
        }
    };

    const getStatusColor = (status: TestRunItemStatus) => {
        switch (status) {
            case TestRunItemStatus.PASSED:
                return 'success';
            case TestRunItemStatus.FAILED:
                return 'error';
            case TestRunItemStatus.BLOCKED:
                return 'warning';
            case TestRunItemStatus.SKIPPED:
                return 'default';
            default:
                return 'processing';
        }
    };

    const buildTreeData = (items: TestRunItem[]) => {
        const folderMap = new Map();
        const rootItems: any[] = [];

        items.forEach(item => {
            const folderId = item.folder_id;
            const folderName = item.folder?.name || 'Root';
            
            if (!folderMap.has(folderId)) {
                const folderNode = {
                    key: `folder-${folderId}`,
                    title: folderName,
                    children: [],
                    isFolder: true
                };
                folderMap.set(folderId, folderNode);
                rootItems.push(folderNode);
            }

            const folderNode = folderMap.get(folderId);
            const itemNode = {
                key: `item-${item.id}`,
                title: (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <span>{item.test_item?.name}</span>
                        <Space>
                            <Select
                                value={item.status}
                                onChange={(value) => handleStatusChange(item.id, value)}
                                style={{ width: 120 }}
                                disabled={!testRun || testRun.status !== TestRunStatus.IN_PROGRESS}
                            >
                                {Object.values(TestRunItemStatus).map(status => (
                                    <Option key={status} value={status}>
                                        <Tag color={getStatusColor(status)}>
                                            {t(`testRuns.itemStatus.${status}`)}
                                        </Tag>
                                    </Option>
                                ))}
                            </Select>
                            <Select
                                style={{ width: 200 }}
                                placeholder={t('testRuns.assignUser')}
                                onChange={(value) => handleAssignUser(item.id, value)}
                                value={item.assigned_to?.id}
                                suffixIcon={<UserOutlined />}
                                allowClear
                            >
                                {users.map(user => (
                                    <Option key={user.id} value={user.id}>
                                        {user.first_name} {user.last_name}
                                    </Option>
                                ))}
                            </Select>
                        </Space>
                    </div>
                ),
                children: item.shared_steps?.map((step: { id: number; name: string }) => ({
                    key: `step-${step.id}`,
                    title: step.name,
                    isLeaf: true
                })) || []
            };
            folderNode.children.push(itemNode);
        });

        return rootItems;
    };

    if (!testRun) {
        return <div>Loading...</div>;
    }

    return (
        <div style={{ padding: '24px' }}>
            <Card>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                    <Title level={2}>{testRun.name}</Title>
                    <Space>
                        {testRun.status === TestRunStatus.DRAFT && (
                            <Button
                                type="primary"
                                icon={<PlayCircleOutlined />}
                                onClick={handleStart}
                            >
                                {t('testRuns.start')}
                            </Button>
                        )}
                        {testRun.status === TestRunStatus.IN_PROGRESS && (
                            <>
                                <Button
                                    type="primary"
                                    icon={<PlayCircleOutlined />}
                                    onClick={() => navigate(`/test-runs/${testRun.id}/execute`)}
                                >
                                    {t('testRuns.execute')}
                                </Button>
                                <Button
                                    type="primary"
                                    icon={<CheckCircleOutlined />}
                                    onClick={handleComplete}
                                >
                                    {t('testRuns.complete')}
                                </Button>
                            </>
                        )}
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => setIsAddModalVisible(true)}
                        >
                            {t('testRuns.addItems')}
                        </Button>
                    </Space>
                </div>

                <Descriptions bordered style={{ marginBottom: '24px' }}>
                    <Descriptions.Item label={t('testRuns.project')}>
                        {testRun.project_name}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('testRuns.status')}>
                        {t(`testRuns.status.${testRun.status}`)}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('testRuns.environment')}>
                        {testRun.environment}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('testRuns.description')} span={3}>
                        {testRun.description}
                    </Descriptions.Item>
                </Descriptions>

                <Tree
                    treeData={buildTreeData(testRun.test_run_items || [])}
                    defaultExpandAll
                />
            </Card>

            <AddTestItemsModal
                visible={isAddModalVisible}
                onCancel={() => setIsAddModalVisible(false)}
                onSuccess={() => {
                    setIsAddModalVisible(false);
                    fetchTestRun(testRun.id);
                }}
                testRunId={testRun.id}
                projectId={testRun.project_id}
            />
        </div>
    );
};

export default TestRunPage; 